import type { FederalState, School } from '@kimuno/api-sdk';
import { Box, Typography } from '@mui/material';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import * as yup from 'yup';

import { updateMe as updateMeAction } from '../../actions/user.actions';
import { palette, spaces } from '../../assets/styles/themes/tokens';
import ConfirmDialog from '../../components/Dialog/ConfirmationDialog';
import NotificationAlert from '../../components/NotificationAlert';
import type { Alert } from '../../components/NotificationAlert/NotificationAlert';
import ProfileHeader from '../../components/ProfilePage/ProfileHeader';
import SubjectsDialog from '../../components/ProfilePage/SubjectsDialog';
import SettingItem from '../../components/SettingItem';
import ChipsSelectorField from '../../components/SettingItem/ChipsSelectorField';
import EditableTextField from '../../components/SettingItem/EditableTextField';
import SchoolAutocompleteField from '../../components/SettingItem/SchoolAutocompleteField';
import SubscriptionNewsLetter from '../../components/SubscriptionNewsLetter';
import constants, {
	gradeAvatarMap,
	NONE_OF_THE_ABOVE_OPTION,
	roleAvatarMap,
} from '../../constants';
import { selectUser } from '../../reducers/user.selectors';
import services from '../../services';
import useAction from '../../utils/useAction';
import useScreenSize from '../../utils/useScreenSize';

interface ProfilePayload {
	firstName: string;
	lastName?: string;
	teacherRoles?: string[];
	schoolStages?: string[];
	federalState?: string[];
	subjects?: string[];
	school?: School;
}

const ProfilePage: React.FC = () => {
	const { t } = useTranslation();
	const user = useSelector(selectUser);
	const updateMe = useAction(updateMeAction);
	const { isXsScreen, isSmScreen, isMdScreen, isLgScreen } = useScreenSize();
	const [isEditMode, setIsEditMode] = useState(false);
	const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
	const [isSujectsDialogOpen, setIsSubjecsDialogOpen] = useState(false);
	const [alert, setAlert] = useState<Alert | undefined>();
	const [federalStates, setFederalStates] = useState<string[]>([]);

	const roles = Object.keys(roleAvatarMap);
	const schoolStagesOptions = Object.keys(gradeAvatarMap);
	const subjectList = constants.subjects
		.filter((subject) => subject.id !== NONE_OF_THE_ABOVE_OPTION)
		.map((subject) => subject.text)
		.sort((a, b) => a.localeCompare(b));

	const {
		firstName = '',
		lastName = '',
		teacherRoles = [],
		schoolStages = [],
		federalState,
		subjectsFlattened = [],
		isSubscribedToNewsletter,
		school,
	} = user ?? {};

	const subjects = useMemo(
		() => subjectsFlattened.map((subject) => subject.text),
		[subjectsFlattened]
	);

	const stableTeacherRoles = useMemo(() => JSON.stringify(teacherRoles), [teacherRoles]);
	const stableSchoolStages = useMemo(() => JSON.stringify(schoolStages), [schoolStages]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	const defaultValues: ProfilePayload = useMemo(
		() => ({
			firstName,
			lastName,
			teacherRoles: teacherRoles ?? [],
			schoolStages: schoolStages ?? [],
			federalState: federalState ? [federalState] : [],
			subjects: subjects.length > 0 ? subjects : [],
			school: school
				? {
						id: school.placeId,
						name: school.name,
						formattedAddress: school.formattedAddress,
						state: school.state,
					}
				: undefined,
		}),
		[firstName, lastName, stableTeacherRoles, stableSchoolStages, federalState, subjects, school]
	);

	const formValidator = yup.object({
		firstName: yup.string().required('This field is required'),
		subjects: yup
			.array()
			.of(yup.string())
			.min(1, 'Required field. Please select the school subjects that interest you.'),
	});

	const { control, handleSubmit, reset, watch, setValue, errors } = useForm<ProfilePayload>({
		mode: 'onBlur',
		validationSchema: formValidator,
		defaultValues,
	});

	const watchedSubjects = watch('subjects') || [];

	// Reset form when user is loaded
	useEffect(() => {
		if (user) {
			reset(defaultValues);
		}
	}, [user, reset, defaultValues]);

	const handleEdit = useCallback(() => setIsEditMode(true), []);

	const handleCancel = useCallback(() => {
		setIsConfirmDialogOpen(true);
	}, []);

	const handleConfirmDialog = useCallback(() => {
		reset(defaultValues);
		setIsEditMode(false);
		setIsConfirmDialogOpen(false);
	}, [reset, defaultValues]);

	const getSubjectsPayload = (selectedSubjects: string[]) => {
		const subjectsOrder = selectedSubjects
			.map((name) => constants.subjects.find((s) => s.text === name))
			.filter((subject): subject is (typeof constants.subjects)[number] => subject !== undefined);
		return {
			majors: subjectsOrder.map((subject, i) => ({
				subject: subject.id,
				priority: i,
			})),
			minors: [],
			prioritized: false,
		};
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (isEditMode && federalStates.length === 0) {
			services.text.fetchFederalStates().then((states: FederalState[]) => {
				setFederalStates(states.map((state) => state.name));
			});
		}
	}, [isEditMode]);

	const handleSave = handleSubmit(async (data) => {
		try {
			const subjects = getSubjectsPayload(data.subjects || []);

			const payload = {
				...data,
				schoolStages: data?.schoolStages ?? [],
				federalState: data?.federalState?.[0] || '',
				subjects,
				isTrainee: data?.teacherRoles?.includes('Referendar:in'),
				school: data.school
					? {
							placeId: data.school.id,
							name: data.school.name,
							formattedAddress: data.school.formattedAddress,
							state: data.school.state,
						}
					: undefined,
			};
			await updateMe(payload);
			reset({
				...data,
				schoolStages: data?.schoolStages ?? [],
				federalState: data?.federalState ?? [],
				subjects: data.subjects ?? [],
				school: data?.school,
			});
			setIsEditMode(false);
			setAlert({
				message: t('All changes were saved successfully'),
				severity: 'success',
			});
		} catch {
			setAlert({
				message: t('An error occurred while saving changes'),
				severity: 'error',
			});
		}
	});

	const getPaddingX = () => {
		if (isLgScreen) return spaces.xl;
		if (isMdScreen || isSmScreen) return spaces.lg;
		return spaces.md;
	};

	const renderAlertComponent = (alert: Alert) => (
		<NotificationAlert
			message={alert.message}
			severity={alert.severity}
			onClose={() => setAlert?.(undefined)}
			alertSx={{ width: '100%' }}
		/>
	);

	if (!user) return null;

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				height: isEditMode ? 'auto' : '100%',
				gap: spaces.md,
				backgroundColor: palette.background.paperElevation1,
				paddingX: getPaddingX(),
				paddingBottom: spaces.xxxxxl,
			}}
		>
			<Box sx={{ position: 'relative' }}>
				{alert && renderAlertComponent(alert)}
				<ProfileHeader
					isEditMode={isEditMode}
					onEdit={handleEdit}
					onCancel={handleCancel}
					onSave={handleSave}
				/>
				<Box
					sx={{
						display: 'grid',
						gridTemplateColumns: isXsScreen || isSmScreen ? '1fr' : '1fr 1fr',
						gap: spaces.md,
						alignItems: 'stretch',
					}}
				>
					<SettingItem title={isEditMode ? `${t('First Name')} *` : t('First Name')}>
						<EditableTextField
							name="firstName"
							control={control}
							defaultValue={firstName}
							isEditMode={isEditMode}
							error={typeof errors.firstName?.message === 'string' ? errors.firstName?.message : undefined}
						/>
					</SettingItem>
					<SettingItem title={t('Last Name')}>
						<EditableTextField
							name="lastName"
							control={control}
							defaultValue={lastName}
							isEditMode={isEditMode}
						/>
					</SettingItem>
					<SettingItem title={t('Rolle')}>
						<ChipsSelectorField
							name="teacherRoles"
							control={control}
							options={roles}
							max={3}
							defaultValue={teacherRoles}
							isEditMode={isEditMode}
						/>
					</SettingItem>
					<SettingItem title={t('School Stage')}>
						<ChipsSelectorField
							name="schoolStages"
							control={control}
							options={schoolStagesOptions}
							defaultValue={schoolStages}
							isEditMode={isEditMode}
						/>
					</SettingItem>
					<SettingItem
						title={t('School subjects')}
						isEditMode={isEditMode}
						button={{
							label: t('Sort by importance'),
							onClick: () => setIsSubjecsDialogOpen(true),
							disabled: watchedSubjects.length === 0,
						}}
					>
						<ChipsSelectorField
							name="subjects"
							control={control}
							options={subjectList}
							defaultValue={subjects}
							isEditMode={isEditMode}
							error={typeof errors.subjects?.message === 'string' ? errors.subjects.message : undefined}
						/>
					</SettingItem>
					<SettingItem title={t('Federal State')}>
						<ChipsSelectorField
							name="federalState"
							control={control}
							options={federalStates}
							max={1}
							defaultValue={federalState ? [federalState] : []}
							isEditMode={isEditMode}
						/>
					</SettingItem>
					<SettingItem title={t('School')}>
						<SchoolAutocompleteField
							name="school"
							control={control}
							setValue={setValue}
							defaultValue={defaultValues.school}
							isEditMode={isEditMode}
						/>
					</SettingItem>
				</Box>
			</Box>
			<Box sx={{ display: 'flex', flexDirection: 'column' }}>
				<Typography variant="h6" color={palette.text.primary} sx={{ paddingY: spaces.md }}>
					{t('Notifications')}
				</Typography>
				<SubscriptionNewsLetter initialValue={isSubscribedToNewsletter} />
			</Box>
			<ConfirmDialog
				isOpen={isConfirmDialogOpen}
				title={t('Quit editing without saving')}
				message={t('Changes you made may not be saved. Are you sure you want to cancel your changes?')}
				onConfirm={handleConfirmDialog}
				onCloseModal={() => setIsConfirmDialogOpen(false)}
				confirmButtonText={t('Discard')}
				confirmButtonColor="error"
			/>
			<SubjectsDialog
				subjects={watchedSubjects}
				open={isSujectsDialogOpen}
				onClose={() => setIsSubjecsDialogOpen(false)}
				onConfirm={(value) => {
					setValue('subjects', value);
					setIsSubjecsDialogOpen(false);
				}}
			/>
		</Box>
	);
};

export default ProfilePage;
